import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Account {
  id: string;
  name: string;
}

interface AddHoldingModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddHoldingModal({ open, onClose, onSuccess }: AddHoldingModalProps) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [accountId, setAccountId] = useState('');
  const [assetClass, setAssetClass] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      supabase.from('investment_accounts').select('id, name').then(({ data }) => setAccounts(data || []));
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!symbol.trim()) {
      setError('Symbol is required');
      return;
    }
    const qty = Number(quantity);
    const cost = Number(costBasis);
    const price = Number(currentPrice);
    if (isNaN(qty) || qty <= 0) {
      setError('Valid quantity is required');
      return;
    }
    if (isNaN(cost) || cost < 0) {
      setError('Valid cost basis is required');
      return;
    }
    if (isNaN(price) || price < 0) {
      setError('Valid current price is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: insertError } = await supabase.from('investment_holdings').insert({
        user_id: user.id,
        account_id: accountId || null,
        symbol: symbol.trim().toUpperCase(),
        name: name.trim() || null,
        quantity: qty,
        cost_basis: cost,
        current_price: price,
        asset_class: assetClass || null,
      });

      if (insertError) throw insertError;

      setSymbol('');
      setName('');
      setQuantity('');
      setCostBasis('');
      setCurrentPrice('');
      setAccountId('');
      setAssetClass('');
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add holding');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Holding</DialogTitle>
          <DialogDescription>Add a stock, fund, or other investment holding.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Symbol</Label>
            <Input id="symbol" value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="e.g. AAPL" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="holding-name">Name (optional)</Label>
            <Input id="holding-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Apple Inc." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="account">Account (optional)</Label>
            <select
              id="account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">No account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input id="quantity" type="number" step="0.0001" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cost-basis">Cost Basis / Share</Label>
              <Input id="cost-basis" type="number" step="0.01" min="0" value={costBasis} onChange={(e) => setCostBasis(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="current-price">Current Price / Share</Label>
            <Input id="current-price" type="number" step="0.01" min="0" value={currentPrice} onChange={(e) => setCurrentPrice(e.target.value)} placeholder="0.00" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="asset-class">Asset Class (optional)</Label>
            <Input id="asset-class" value={assetClass} onChange={(e) => setAssetClass(e.target.value)} placeholder="e.g. Stocks, Bonds" />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Adding...' : 'Add Holding'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
